import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ApiError, NetworkError, api } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { PasswordInput } from '@/components/PasswordInput';
import { AuthLayout } from './AuthLayout';

/**
 * FE-1 — the single login page.
 *
 * **There is no Employee/Admin toggle.** The mockup shows one; it is a demo device
 * and must not ship (§2). Role is resolved from the authenticated account, and the
 * post-login destination comes from the server as `landingPath` — the browser never
 * decides where an admin lands.
 *
 * Microsoft SSO is the primary action, email/password secondary, matching §5.
 */
export function LoginPage() {
  const { signIn, publicConfig } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [ssoStarting, setSsoStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [capsLock, setCapsLock] = useState(false);

  // Where to go after signing in, if a guard bounced them here.
  const intended = (location.state as { from?: string } | null)?.from;

  // Surfaced when the SSO round trip fails and redirects back with a reason.
  useEffect(() => {
    const reason = new URLSearchParams(location.search).get('error');
    if (reason) setError(decodeURIComponent(reason));
  }, [location.search]);

  async function handlePasswordSignIn(event: React.FormEvent) {
    event.preventDefault();
    if (submitting) return;

    setError(null);
    setSubmitting(true);
    try {
      const me = await signIn(email, password);
      navigate(intended ?? me.landingPath, { replace: true });
    } catch (cause) {
      // §5.2 — surface the server's generic message verbatim. Distinguishing
      // "no such account" from "wrong password" here would leak who is enrolled.
      if (cause instanceof ApiError) setError(cause.message);
      else if (cause instanceof NetworkError) setError(cause.message);
      else setError('Something went wrong. Please try again.');
      setPassword('');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleMicrosoftSignIn() {
    setError(null);
    setSsoStarting(true);
    try {
      // The backend builds the authorize URL and keeps the PKCE verifier and nonce
      // server-side (§5.1), so a compromised page cannot complete the flow elsewhere.
      const { url } = await api<{ url: string; state: string }>('/auth/microsoft/start');
      window.location.assign(url);
    } catch (cause) {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Microsoft sign-in is unavailable right now.',
      );
      setSsoStarting(false);
    }
  }

  const ssoEnabled = publicConfig?.microsoftSsoEnabled ?? false;
  const passwordEnabled = publicConfig?.passwordLoginEnabled ?? true;

  return (
    <AuthLayout
      title="Sign in to Ambition"
      subtitle="A three-minute daily check-in on how you actually led today."
    >
      {error && (
        <div
          className="mb-5 rounded-control border border-[#e0796b]/45 bg-[#e0796b]/12 px-3.5 py-3 text-sm text-ink"
          role="alert"
        >
          {error}
        </div>
      )}

      {ssoEnabled && (
        <>
          <button
            type="button"
            onClick={() => void handleMicrosoftSignIn()}
            disabled={ssoStarting}
            className="btn-primary w-full"
          >
            <MicrosoftLogo />
            {ssoStarting ? 'Redirecting…' : 'Sign in with Microsoft'}
          </button>

          {passwordEnabled && (
            <div className="my-5 flex items-center gap-3" aria-hidden="true">
              <span className="h-px flex-1 bg-line" />
              <span className="text-xs uppercase tracking-wider text-muted">or</span>
              <span className="h-px flex-1 bg-line" />
            </div>
          )}
        </>
      )}

      {passwordEnabled ? (
        <form onSubmit={handlePasswordSignIn} noValidate>
          <div className="mb-4">
            <label className="label" htmlFor="email">
              Work email
            </label>
            <input
              id="email"
              type="email"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              required
              placeholder="you@company.com"
            />
          </div>

          <div className="mb-2">
            <div className="mb-1.5 flex items-baseline justify-between">
              <label className="label mb-0" htmlFor="password">
                Password
              </label>
              <Link
                to="/forgot-password"
                className="text-xs font-medium text-muted hover:text-accent"
              >
                Forgot password?
              </Link>
            </div>
            <PasswordInput
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              // FE-1-8 — caps lock is the most common cause of a "wrong password"
              // that is not actually wrong.
              onKeyUp={(e) => setCapsLock(e.getModifierState?.('CapsLock') ?? false)}
              autoComplete="current-password"
              required
            />
            {capsLock && (
              <p className="mt-1.5 text-xs text-secondary" role="status">
                Caps Lock is on.
              </p>
            )}
          </div>

          <button type="submit" disabled={submitting} className="btn-primary mt-4 w-full">
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      ) : (
        !ssoEnabled && (
          <p className="text-sm text-muted">
            No sign-in method is enabled. Contact your programme admin.
          </p>
        )
      )}

      {!passwordEnabled && ssoEnabled && (
        <p className="mt-4 text-center text-xs text-muted">
          Your organisation requires signing in with Microsoft.
        </p>
      )}
    </AuthLayout>
  );
}

function MicrosoftLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <rect x="0" y="0" width="7" height="7" fill="#F25022" />
      <rect x="9" y="0" width="7" height="7" fill="#7FBA00" />
      <rect x="0" y="9" width="7" height="7" fill="#00A4EF" />
      <rect x="9" y="9" width="7" height="7" fill="#FFB900" />
    </svg>
  );
}
